import React from "react"
import SampleCard from "./SampleCard"
import CollectionCard from "./CollectionCard"
import LoadMoreButton from "./LoadMoreButton"
import sampleStore from "../stores/sampleStore"
import collectionStore from "../stores/collectionStore"
import userStore from "../stores/userStore"
import User from "../models/user"
import Sample from "../models/sample"
import Collection from "../models/collection"

import _ from "lodash"

import { observer, Provider } from "mobx-react"
import { VariableSizeList as List } from 'react-window'
import AutoSizer from 'react-virtualized-auto-sizer'

// Define a memoized VirtualizedRow component inline
const VirtualizedRow = React.memo(({ row, style, renderSampleRow, renderCollectionRow, showSampleSelect }) => {
  let content = null;

  if (row && row[0] instanceof Sample) {
    content = renderSampleRow(row, showSampleSelect);
  } else if (row && row[0] instanceof Collection) {
    content = renderCollectionRow(row);
  }
  return (
    <div style={{ ...style, height: 'auto' }}>
      {content}
    </div>
  );
});

const stores = {
  sampleStore,
  collectionStore,
  userStore,
}

@observer
export default class SamplesAndCollections extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      isClient: false,
      showSampleSelect: false,
      showSequencer: false
    }
    this.listRef = React.createRef()
    this.samplesAndCollectionsRows = []

    if (props.currentUser) {
      userStore.currentUser = new User(props.currentUser)
    }
    sampleStore.samples = props.samples.map(
      (sample) => new Sample(sample)
    )
    collectionStore.visibleCollections = props.collections.map(
      (collection) => new Collection(collection)
    )
  }

  componentDidMount() {
    this.setState({ isClient: true })
    window.addEventListener('resize', this.handleResize)

    const { currentUser, samples, collections } = this.props
    if (currentUser) {
      userStore.currentUser = new User(currentUser)
    }
    sampleStore.samples = samples.map(
      (sample) => new Sample(sample)
    )
    collectionStore.visibleCollections = collections.map(
      (collection) => new Collection(collection)
    )
    // Load selectedSamples on the client side after hydration
    sampleStore.loadFromLocalStorage();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize)
  }

  handleResize = () => {
    if (this.listRef.current) {
      this.listRef.current.resetAfterIndex(0)
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      sampleStore.samples.length !== prevProps.samples.length ||
      sampleStore.showSampleSelect !== prevState.showSampleSelect ||
      sampleStore.showSequencer !== prevState.showSequencer ||
      collectionStore.visibleCollections.length !== prevProps.collections.length
    ) {
      if (this.listRef.current) {
        this.listRef.current.resetAfterIndex(0, true); // Pass true to force re-render
      }
    }
  }

  renderSampleRow(samples, showSampleSelect) {
    const sampleSlugs = samples.map((sample) => sample.slug)
    return (
      <div className="row" key={sampleSlugs.join('-')}>
        {samples.map((sample) => {
          return <SampleCard sample={sample} key={sample.slug} showSampleSelect={showSampleSelect} />
        })}
      </div>
    )
  }

  renderCollectionRow = (collections) => {
    const collectionSlugs = collections.map(collection => collection.slug)
    return (
      <div className="row" key={collectionSlugs.join("-")}>
        {collections.map((collection) => {
          return <CollectionCard collection={collection} key={collection.slug} />
        })}
      </div>
    )
  }

  // Use the memoized VirtualizedRow for better performance
  renderRow = ({ index, style }) => {
    const row = this.samplesAndCollectionsRows[index]
    const showSampleSelect = sampleStore.showSampleSelect || sampleStore.showSequencer;

    return (
      <VirtualizedRow
        row={row}
        style={style}
        renderSampleRow={(row) => this.renderSampleRow(row, showSampleSelect)}
        renderCollectionRow={this.renderCollectionRow.bind(this)}
        showSampleSelect={showSampleSelect}
      />
    )
  }

  getRowHeight = index => {
    const screenWidth = window.innerWidth;
    const row = this.samplesAndCollectionsRows[index];
    const verySmallMobileScreenWidth = 400;
    const smallMobileScreenWidth = 500;
    const mobileScreenWidth = 600;

    if (screenWidth <= verySmallMobileScreenWidth) {
      if (row && row[0] instanceof Collection) {
        return 640;
      }
      return 1330;
    } else if (screenWidth <= smallMobileScreenWidth) {
      if (row && row[0] instanceof Collection) {
        return 650;
      }
      return 1330;
    } else if (screenWidth <= mobileScreenWidth) {
      if (row && row[0] instanceof Collection) {
        return 642;
      }
      return 1245;
    } else {
      if (row && row[0] instanceof Collection) {
        return 320;
      }
      return 349;
    }
  }

  getTotalHeight = () => {
    return Array.from({ length: this.samplesAndCollectionsRows.length })
      .reduce((total, _, index) => total + this.getRowHeight(index), 0)
  }

  render() {
    const { pagyMetadata } = this.props
    const samples = sampleStore.samples
    const samplesCount = samples.length
    const sampleRows = []
    let currentRow = []

    samples.forEach((sample, idx) => {
      currentRow.push(sample)
      if (currentRow.length === 4 || samplesCount - 1 === idx) {
        sampleRows.push(currentRow)
        currentRow = []
      }
    })
    const collections = _.uniqBy(collectionStore.visibleCollections, 'slug')
    const collectionRows = []
    let currentCollectionRow = []
    collections.forEach((collection, idx) => {
      currentCollectionRow.push(collection)
      if (currentCollectionRow.length === 4 || collections.length - 1 === idx) {
        collectionRows.push(currentCollectionRow)
        currentCollectionRow = []
      }
    })

    const sampleRowsChunks = _.chunk(sampleRows, 4)
    sampleRowsChunks.forEach((sampleRowChunk, idx) => {
      if (collectionRows[idx]) {
        sampleRowChunk.splice(2, 0, collectionRows[idx])
      }
    })
    this.samplesAndCollectionsRows = _.flatten(sampleRowsChunks)

    const { isClient, showSampleSelect, showSequencer } = this.state

    if (showSampleSelect !== sampleStore.showSampleSelect ||
        showSequencer !== sampleStore.showSequencer) {
      this.setState({
        showSampleSelect: sampleStore.showSampleSelect,
        showSequencer: sampleStore.showSequencer
      });
    }

    // Generate stable keys for SSR
    const generateRowKey = (row, index) => {
        if (!row || row.length === 0) return `ssr-row-${index}`;
        const type = row[0] instanceof Sample ? 'sample' : 'collection';
        return `ssr-row-${type}-${row.map(item => item.slug).join('-')}`;
    }

    return (
      <Provider {...stores}>
        <div className="container" id="samples_and_collections">
          {!isClient && (
            <div className="ssr-content">
              {this.samplesAndCollectionsRows.map((row, index) => (
                <div key={index}>
                  {row && row[0] instanceof Sample && this.renderSampleRow(row, showSampleSelect)}
                  {row && row[0] instanceof Collection && this.renderCollectionRow(row)}
                </div>
              ))}
            </div>
          )}

          {isClient && (
            <AutoSizer disableHeight>
              {({ width }) => (
                <List
                  ref={this.listRef}
                  height={this.getTotalHeight()}
                  itemCount={this.samplesAndCollectionsRows.length}
                  itemSize={this.getRowHeight}
                  width={width}
                  overscanCount={3}
                >
                  {this.renderRow}
                </List>
              )}
            </AutoSizer>
          )}
        </div>
        <LoadMoreButton pagyMetadata={pagyMetadata} />
      </Provider>
    )
  }
}